import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { CartItem } from '../types';
import { useAuth } from './AuthContext';
import { useToast } from './ToastContext';

interface CartContextType {
  items: CartItem[];
  itemCount: number;
  subtotalCents: number;
  loading: boolean;
  isCartOpen: boolean;
  openCart: () => void;
  closeCart: () => void;
  addToCart: (variantId: string, quantity?: number) => Promise<boolean>;
  updateQuantity: (cartItemId: string, quantity: number) => Promise<void>;
  removeItem: (cartItemId: string) => Promise<void>;
  refreshCart: () => Promise<void>;
}

const ANON_CART_KEY = 'aura_anon_cart_token';

const CartContext = createContext<CartContextType | undefined>(undefined);

export function CartProvider({ children }: { children: React.ReactNode }) {
  const [items, setItems] = useState<CartItem[]>([]);
  const [subtotalCents, setSubtotalCents] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const { user } = useAuth();
  const { showToast } = useToast();

  const getAnonToken = (): string => {
    let token = localStorage.getItem(ANON_CART_KEY);
    if (!token) {
      token = 'anon_' + Math.random().toString(36).substring(2) + Date.now().toString(36);
      localStorage.setItem(ANON_CART_KEY, token);
    }
    return token;
  };

  const refreshCart = useCallback(async () => {
    try {
      const anonToken = getAnonToken();
      const res = await fetch(`/api/cart?anon_token=${encodeURIComponent(anonToken)}`);
      if (res.ok) {
        const data = (await res.json()) as any;
        setItems(data.items || []);
        setSubtotalCents(data.subtotal_cents || 0);
      }
    } catch (err) {
      console.warn('Failed to load cart', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Merge cart upon user login
  useEffect(() => {
    if (user) {
      const anonToken = localStorage.getItem(ANON_CART_KEY);
      if (anonToken) {
        fetch('/api/cart/merge', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ anon_token: anonToken }),
        })
          .then(() => {
            localStorage.removeItem(ANON_CART_KEY);
            refreshCart();
          })
          .catch(console.error);
      } else {
        refreshCart();
      }
    } else {
      refreshCart();
    }
  }, [user, refreshCart]);

  const addToCart = async (variantId: string, quantity: number = 1): Promise<boolean> => {
    try {
      const anonToken = getAnonToken();
      const res = await fetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variant_id: variantId,
          quantity,
          anon_token: anonToken,
        }),
      });

      const data = (await res.json()) as any;
      if (!res.ok) {
        showToast('error', 'Não foi possível adicionar ao carrinho', data.error);
        return false;
      }

      await refreshCart();
      showToast('success', 'Adicionado à sacola', 'O item foi incluído no seu carrinho com sucesso.');
      setIsCartOpen(true);
      return true;
    } catch {
      showToast('error', 'Erro', 'Falha ao conectar com o carrinho.');
      return false;
    }
  };

  const updateQuantity = async (cartItemId: string, quantity: number) => {
    if (quantity <= 0) {
      await removeItem(cartItemId);
      return;
    }

    const item = items.find((i) => i.cart_item_id === cartItemId);
    if (!item) return;

    try {
      const anonToken = getAnonToken();
      const res = await fetch('/api/cart', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          variant_id: item.variant_id,
          quantity,
          anon_token: anonToken,
        }),
      });

      if (res.ok) {
        await refreshCart();
      } else {
        const data = (await res.json()) as any;
        showToast('error', 'Limite de estoque', data.error);
      }
    } catch {
      showToast('error', 'Erro', 'Não foi possível atualizar a quantidade.');
    }
  };

  const removeItem = async (cartItemId: string) => {
    try {
      const res = await fetch(`/api/cart/items/${cartItemId}`, { method: 'DELETE' });
      if (res.ok) {
        await refreshCart();
        showToast('info', 'Item removido', 'O produto foi retirado da sacola.');
      }
    } catch {
      showToast('error', 'Erro', 'Falha ao remover item.');
    }
  };

  const itemCount = items.reduce((acc, curr) => acc + curr.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        items,
        itemCount,
        subtotalCents,
        loading,
        isCartOpen,
        openCart: () => setIsCartOpen(true),
        closeCart: () => setIsCartOpen(false),
        addToCart,
        updateQuantity,
        removeItem,
        refreshCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart(): CartContextType {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
}
